import { useEffect, useState } from 'react';

import {
	Card,
	CardHeader,
	CardContent,
	Typography,
	FormControlLabel,
	Select,
	Switch,
	MenuItem,
	Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ConfirmationDialog } from '~components';
import { useAuthorize } from '~features/authentication';
import { PaymentTypeEnum } from '~features/finances';

import { useStyles } from './style';
import { useWizardFormField, useDebouncedWizardSave } from '../../../../../../shared/hooks';

interface PaymentConfigurationProps {
	save?(...args: unknown[]): unknown;
	requireHostConfirmation?: boolean;
	depositAmountRequired?: boolean;
	personalBillingDeposit?: number;
	allowPersonalBilling?: boolean;
	setAllowPersonalBilling?(...args: unknown[]): unknown;
	supportedPaymentType: PaymentTypeEnum;
	setSupportedPaymentType?(...args: unknown[]): unknown;
}

interface UserAction {
	type: 'confirmDisableHostConfirmation';
	value: PaymentTypeEnum;
}

const PaymentConfiguration = (props: PaymentConfigurationProps) => {
	const classes = useStyles();

	const {
		save,
		requireHostConfirmation,
		depositAmountRequired,
		personalBillingDeposit: savedPersonalBillingDeposit,

		allowPersonalBilling,
		setAllowPersonalBilling,

		supportedPaymentType,
		setSupportedPaymentType,
	} = props;

	const { t } = useTranslation();
	const [userAction, setUserAction] = useState<UserAction | null>(null);

	const openDepositAmount = depositAmountRequired || false;

	useEffect(() => {
		save({ depositAmountRequired: openDepositAmount });
	}, [openDepositAmount]);

	useEffect(() => {
		save({
			personalBilling: allowPersonalBilling,
		});
	}, [allowPersonalBilling]);

	const personalBillingDeposit = useWizardFormField(savedPersonalBillingDeposit, {
		required: false,
		isNumeric: true,
		minAmount: 0,
		maxAmount: 5000,
		maxLength: 4,
	});
	useDebouncedWizardSave(
		'personalBillingDeposit',
		parseInt(personalBillingDeposit.value),
		personalBillingDeposit.isValid,
		save,
		300,
		personalBillingDeposit.isRequired
	);

	useEffect(() => {
		personalBillingDeposit.setRequired(openDepositAmount);
	}, [openDepositAmount]);

	// DEPOSIT INFORMATION BLOCK
	// const handleToggleOpenDepositAmount = () => {
	//   setOpenDepositAmount(!openDepositAmount);
	// };

	const handlePersonalBilling = () => {
		setAllowPersonalBilling(!allowPersonalBilling);
	};

	const handleSupportedPaymentChange = (newValue: PaymentTypeEnum) => {
		if (requireHostConfirmation && newValue === PaymentTypeEnum.OneOffPayment) {
			setUserAction({ type: 'confirmDisableHostConfirmation', value: newValue });
			return;
		}

		setSupportedPaymentType?.(newValue);
	};

	/* * * * * *
	 * RENDER  *
	 * * * * * */
	return (
		<>
			<Card className={classes.root}>
				<CardHeader
					subheader={t('views.addPolicy.policyConfiguration.category.paymentPolicies.description')}
					title={t('views.addPolicy.policyConfiguration.category.paymentPolicies.title')}
					titleTypographyProps={{
						variant: 'h3',
					}}
				/>
				<CardContent>
					<div className={classes.formGroup}>
						<div className={classes.inputWithExtraLabel}>
							<FormControlLabel
								className={classes.switchLabel}
								control={
									<Switch
										checked={allowPersonalBilling}
										color='primary'
										onChange={handlePersonalBilling}
									/>
								}
								label={
									<div>
										<Typography variant='h5'>{t('ui.label.personalBilling')}</Typography>
										<Typography color='textSecondary'>
											{t('views.userGroupDetail.summary.info.personalBilling')}
										</Typography>
									</div>
								}
								labelPlacement='start'
							/>
						</div>
					</div>

					{allowPersonalBilling && (
						<Stack spacing={1} className={classes.formGroup} sx={{ display: 'flex' }}>
							<Typography variant='h5' sx={{ flexGrow: 1 }}>
								{t('paymentType')}
							</Typography>
							<Typography color='textSecondary'>
								{t('policies.selectPaymentTypeDescription')}
							</Typography>
							<Select
								value={supportedPaymentType}
								onChange={(e) => handleSupportedPaymentChange(e.target.value)}
								sx={{ width: 180 }}
							>
								{Object.values(PaymentTypeEnum).map((el: PaymentTypeEnum) => (
									<MenuItem key={el} value={el}>
										{t(el === PaymentTypeEnum.BillingPeriod ? 'directDebit' : el)}
									</MenuItem>
								))}
							</Select>
						</Stack>
					)}
				</CardContent>
			</Card>

			<ConfirmationDialog
				open={userAction?.type === 'confirmDisableHostConfirmation'}
				onConfirm={() => {
					save({ updatedHostConfirmation: false });
					setSupportedPaymentType?.(userAction?.value);
					setUserAction(null);
				}}
				title={t('changeResource', { resource: t('nav.topic.settings') })}
				subTitle={`${t('thisWillDisableSetting')} "${t('views.addPolicy.bookingConfiguration.section.hostConfirmation.title')}"`}
				onClose={() => setUserAction(null)}
			/>
		</>
	);
};

export default PaymentConfiguration;
